import { useState, useEffect } from "react";
import { useAuth } from "../lib/AuthContext";
import { apiGet } from "../lib/api";
import { COLORS } from "../lib/colors";

// Prototype color overrides
const C = {
  bgPrimary: "#0d1117",
  bgSecondary: "#161b22",
  bgTertiary: "#21262d",
  border: "#30363d",
  textPrimary: "#e6edf3",
  textSecondary: "#8b949e",
  textMuted: "#6e7681",
  green: "#238636",
  green3: "#2ea043",
  yellow: "#d29922",
  red: "#da3633",
  blue: "#58a6ff",
  purple: "#a371f7",
};

// Traffic light scoring: 70+ = green "5", 60-69 = green "3", 45-59 = yellow "!", <45 = red cross
function getIndicator(score) {
  if (score >= 70) return { bg: C.green, color: "#fff", label: "5" };
  if (score >= 60) return { bg: C.green3, color: "#fff", label: "3" };
  if (score >= 45) return { bg: C.yellow, color: "#000", label: "!" };
  return { bg: C.red, color: "#fff", label: "\u2715" };
}

function getScoreColor(score) {
  if (score >= 70) return C.green;
  if (score >= 60) return C.green3;
  if (score >= 45) return C.yellow;
  return C.red;
}

// Round indicator (large 32px for categories)
function Indicator({ score, size = 32, fontSize = "0.75rem" }) {
  const ind = getIndicator(score);
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: size, height: size, borderRadius: "50%",
      background: ind.bg, color: ind.color,
      fontSize, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace",
      flexShrink: 0,
    }}>
      {ind.label}
    </div>
  );
}

// Small indicator (24px for sub-metrics / individuals)
function IndicatorSm({ score, label, bg, color: clr }) {
  // Allow explicit override or compute from score
  const ind = (bg && label != null) ? { bg, color: clr || "#fff", label } : getIndicator(score ?? 0);
  return (
    <div style={{
      width: 24, height: 24, borderRadius: "50%",
      display: "flex", alignItems: "center", justifyContent: "center",
      background: ind.bg, color: ind.color,
      fontSize: "0.65rem", fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace",
      flexShrink: 0,
    }}>
      {ind.label}
    </div>
  );
}

function IndicatorNa() {
  return (
    <div style={{
      width: 24, height: 24, borderRadius: "50%",
      display: "flex", alignItems: "center", justifyContent: "center",
      background: C.bgTertiary, border: `1px solid ${C.border}`, color: C.textMuted,
      fontSize: "0.65rem", fontWeight: 700, flexShrink: 0,
    }}>
      ?
    </div>
  );
}

// Source badge (CV / DOC / WEB)
function SourceBadge({ type }) {
  const styles = {
    CV: { bg: "rgba(88, 166, 255, 0.2)", color: C.blue },
    DOC: { bg: "rgba(35, 134, 54, 0.2)", color: C.green },
    WEB: { bg: "rgba(210, 153, 34, 0.2)", color: C.yellow },
  };
  const s = styles[type] || styles.CV;
  return (
    <span style={{
      fontSize: "0.65rem", padding: "0.125rem 0.4rem", borderRadius: 3,
      textTransform: "uppercase", fontWeight: 600, marginLeft: "0.5rem",
      background: s.bg, color: s.color,
    }}>
      {type}
    </span>
  );
}

// Eligibility scale bar
function EligibilityScale({ score }) {
  return (
    <div style={{
      background: C.bgSecondary, border: `1px solid ${C.border}`,
      borderRadius: 12, padding: "1.5rem 2rem", marginBottom: "1.5rem",
    }}>
      {/* Scale bar */}
      <div style={{
        position: "relative", height: 16, borderRadius: 8,
        display: "flex", overflow: "hidden", background: C.bgTertiary,
      }}>
        <div style={{ width: "45%", height: "100%", background: C.red }} />
        <div style={{ width: "15%", height: "100%", background: C.yellow }} />
        <div style={{ width: "10%", height: "100%", background: C.green3 }} />
        <div style={{ width: "30%", height: "100%", background: C.green }} />
        {/* Marker */}
        <div style={{
          position: "absolute", top: -6, left: `${score}%`,
          width: 6, height: 28, background: C.textPrimary,
          borderRadius: 3, transform: "translateX(-50%)",
          boxShadow: "0 0 12px rgba(255,255,255,0.6)",
        }} />
      </div>
      {/* Labels */}
      <div style={{ position: "relative", height: 20, marginTop: "0.5rem", fontSize: "0.75rem", color: C.textMuted }}>
        <span style={{ position: "absolute", left: 0 }}>0</span>
        <span style={{ position: "absolute", left: "45%", transform: "translateX(-50%)" }}>45</span>
        <span style={{ position: "absolute", left: "60%", transform: "translateX(-50%)" }}>60</span>
        <span style={{ position: "absolute", left: "70%", transform: "translateX(-50%)" }}>70</span>
        <span style={{ position: "absolute", right: 0 }}>100</span>
      </div>
      {/* Legend */}
      <div style={{ display: "flex", justifyContent: "center", gap: "2rem", marginTop: "1rem", fontSize: "0.8rem", color: C.textSecondary }}>
        {[
          { color: C.red, label: "Gap" },
          { color: C.yellow, label: "Borderline" },
          { color: C.green3, label: "3-year" },
          { color: C.green, label: "5-year" },
        ].map(item => (
          <span key={item.label} style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <span style={{ width: 12, height: 12, borderRadius: "50%", background: item.color }} />
            {item.label}
          </span>
        ))}
      </div>
    </div>
  );
}

// Sub-metric row
function SubMetric({ indicator, name, value, target }) {
  return (
    <div style={{
      padding: "0.5rem 1.25rem 0.5rem 5.5rem",
      display: "flex", alignItems: "center",
      borderBottom: `1px solid ${C.border}`, fontSize: "0.85rem",
    }}>
      {indicator}
      <span style={{ width: 200, color: C.textSecondary, marginLeft: "0.75rem", flexShrink: 0 }}>{name}</span>
      <span style={{
        fontFamily: "'IBM Plex Mono', monospace", fontSize: "0.9rem",
        color: C.green, fontWeight: 700, width: 100,
      }}>{value}</span>
      <span style={{ flex: 1 }} />
      <span style={{ fontSize: "0.8rem", color: C.textMuted, textAlign: "right" }}>{target}</span>
    </div>
  );
}

// Criterion row (expandable)
function CriterionRow({ code, name, value, indicator, subMetrics, source, rightLabel }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <div
        onClick={() => subMetrics?.length ? setExpanded(!expanded) : null}
        style={{
          padding: "0.75rem 1.25rem 0.75rem 3.5rem",
          display: "flex", alignItems: "center",
          borderBottom: `1px solid ${C.border}`,
          cursor: subMetrics?.length ? "pointer" : "default",
          transition: "background 0.15s",
        }}
        onMouseEnter={e => { if (subMetrics?.length) e.currentTarget.style.background = "rgba(255,255,255,0.02)"; }}
        onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
      >
        {indicator}
        <span style={{
          fontFamily: "'IBM Plex Mono', monospace", fontSize: "0.8rem",
          color: C.textMuted, width: 45, flexShrink: 0, marginLeft: "0.5rem",
        }}>{code}</span>
        <span style={{ fontSize: "0.9rem", fontWeight: 600 }}>{name}</span>
        {source && <SourceBadge type={source} />}
        <span style={{ flex: 1 }} />
        {value && <span style={{
          fontFamily: "'IBM Plex Mono', monospace", fontSize: "0.85rem",
          color: C.green, marginRight: "0.5rem", fontWeight: 700,
        }}>{value}</span>}
        {rightLabel && <span style={{
          fontSize: "0.8rem", color: C.textMuted, marginRight: "0.5rem",
        }}>{rightLabel}</span>}
        {subMetrics?.length > 0 && <span style={{ color: C.textMuted, fontSize: "0.7rem" }}>▲</span>}
      </div>
      {expanded && subMetrics && (
        <div style={{ background: C.bgPrimary, borderBottom: `1px solid ${C.border}` }}>
          {subMetrics.map((sm, i) => (
            <SubMetric key={i} {...sm} />
          ))}
        </div>
      )}
    </>
  );
}

// Individual row (expandable detail)
function IndividualRow({ indicator, name, tag, score, cvBtn, details }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          padding: "0.625rem 1.25rem 0.625rem 4.5rem",
          display: "flex", alignItems: "center",
          borderBottom: `1px solid ${C.border}`,
          cursor: "pointer", transition: "background 0.15s",
        }}
        onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.02)"; }}
        onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
      >
        {indicator}
        <span style={{ flex: 1, fontSize: "0.875rem", marginLeft: "0.75rem" }}>{name}</span>
        {tag && (
          <span style={{
            fontSize: "0.7rem", padding: "0.125rem 0.5rem", borderRadius: 4,
            background: C.bgTertiary, color: C.textMuted, marginLeft: "0.5rem",
          }}>{tag}</span>
        )}
        <span style={{
          fontFamily: "'IBM Plex Mono', monospace", fontSize: "0.85rem",
          color: C.textSecondary, marginRight: "0.75rem", marginLeft: "0.75rem",
        }}>{score} pts</span>
        {cvBtn}
      </div>
      {expanded && details && (
        <div style={{
          background: C.bgTertiary, margin: "0 1.25rem 0.5rem 4.5rem",
          padding: "0.5rem 0", borderRadius: 6, border: `1px solid ${C.border}`,
        }}>
          {details.map((d, i) => (
            <div key={i} style={{
              display: "flex", alignItems: "center",
              padding: "0.5rem 1rem",
              borderBottom: i < details.length - 1 ? `1px solid ${C.border}` : "none",
              fontSize: "0.85rem",
            }}>
              <IndicatorSm bg={d.check === true ? C.green : d.check === false ? C.yellow : C.bgTertiary}
                color={d.check === true ? "#fff" : d.check === false ? "#000" : C.textMuted}
                label={d.check === true ? "✓" : d.check === false ? "!" : "—"} />
              <span style={{
                fontFamily: "'IBM Plex Mono', monospace", fontSize: "0.75rem",
                color: C.textMuted, width: 80, flexShrink: 0, marginLeft: "0.5rem",
              }}>{d.label.split(" ")[0]}</span>
              <span style={{ color: C.textSecondary, flex: 1 }}>{d.label.split(" ").slice(1).join(" ")}</span>
              <span style={{
                fontWeight: 600, color: C.textPrimary, textAlign: "right",
              }}>{d.value}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// Individual list header (collapsible)
function IndividualListHeader({ label, children }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          padding: "0.75rem 1.25rem 0.75rem 3.5rem",
          display: "flex", alignItems: "center", cursor: "pointer",
          background: C.bgPrimary, borderBottom: `1px solid ${C.border}`,
          fontSize: "0.85rem", color: C.textMuted,
        }}
        onMouseEnter={e => { e.currentTarget.style.color = C.textSecondary; }}
        onMouseLeave={e => { e.currentTarget.style.color = C.textMuted; }}
      >
        <span style={{ flex: 1 }}>{label}</span>
        <span style={{
          color: C.textMuted, fontSize: "0.75rem",
          transition: "transform 0.2s",
          transform: expanded ? "rotate(180deg)" : "none",
        }}>{"\u25BC"}</span>
      </div>
      {expanded && (
        <div style={{ background: C.bgPrimary }}>
          {children}
        </div>
      )}
    </>
  );
}

// Category section (collapsible)
function Category({ id, score, title, sourceType, defaultOpen = false, children }) {
  const [expanded, setExpanded] = useState(defaultOpen);
  return (
    <div style={{
      background: C.bgSecondary, border: `1px solid ${C.border}`,
      borderRadius: 10, marginBottom: "0.75rem", overflow: "hidden",
    }}>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          padding: "1rem 1.25rem", display: "flex", alignItems: "center",
          cursor: "pointer", transition: "background 0.15s",
        }}
        onMouseEnter={e => { e.currentTarget.style.background = C.bgTertiary; }}
        onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flex: 1 }}>
          <Indicator score={score} />
          <h3 style={{ fontSize: "1rem", fontWeight: 600, margin: 0 }}>{title}</h3>
          <SourceBadge type={sourceType} />
        </div>
        <div style={{
          display: "flex", alignItems: "center", gap: "0.75rem",
          fontFamily: "'IBM Plex Mono', monospace", color: C.textSecondary,
        }}>
          {score}/100
          <span style={{
            color: C.textMuted, fontSize: "0.75rem",
            transition: "transform 0.2s",
            transform: expanded ? "rotate(180deg)" : "none",
          }}>{"\u25BC"}</span>
        </div>
      </div>
      {expanded && (
        <div style={{ borderTop: `1px solid ${C.border}` }}>
          {children}
        </div>
      )}
    </div>
  );
}

// CV download button
function CvButton({ onClick }) {
  return (
    <button
      onClick={e => { e.stopPropagation(); onClick?.(); }}
      style={{
        background: C.bgTertiary, border: `1px solid ${C.border}`,
        color: C.textMuted, padding: "0.25rem 0.5rem", borderRadius: 4,
        cursor: "pointer", fontSize: "0.75rem", display: "flex",
        alignItems: "center", gap: "0.25rem", transition: "all 0.15s",
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = C.blue; e.currentTarget.style.color = C.blue; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.textMuted; }}
    >
      CV
    </button>
  );
}

// Resource button
function ResourceButton({ icon, label, onClick, disabled }) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        display: "flex", alignItems: "center", gap: "0.5rem",
        padding: "0.75rem 1rem", background: C.bgTertiary,
        border: `1px solid ${C.border}`, borderRadius: 6,
        color: disabled ? C.textMuted : C.textPrimary,
        fontSize: "0.85rem", cursor: disabled ? "not-allowed" : "pointer",
        transition: "all 0.15s", opacity: disabled ? 0.5 : 1,
      }}
      onMouseEnter={e => { if (!disabled) { e.currentTarget.style.borderColor = C.blue; e.currentTarget.style.background = "rgba(88,166,255,0.1)"; } }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = C.bgTertiary; }}
    >
      {icon} {label}
    </button>
  );
}


export default function DashboardPage({ institutionId, programmeId }) {
  const { session } = useAuth();
  const token = session?.access_token;
  const [status, setStatus] = useState(null);
  const [facultyList, setFacultyList] = useState([]);
  const [studentList, setStudentList] = useState([]);
  const [alumniList, setAlumniList] = useState([]);
  const [gapReport, setGapReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    if (!programmeId || !token) { setLoading(false); return; }
    loadAll();
  }, [programmeId, token]);

  const loadAll = async () => {
    try {
      const [statusData, facultyData, studentData, alumniData, gapData] = await Promise.all([
        apiGet(`/programme/${programmeId}/status`, token),
        institutionId ? apiGet(`/faculty/${institutionId}/list`, token) : { faculty: [] },
        apiGet(`/students/${programmeId}/list`, token),
        apiGet(`/alumni/${programmeId}/list`, token),
        apiGet(`/programme/${programmeId}/gap-report`, token).catch(() => null),
      ]);
      setStatus(statusData);
      setFacultyList(facultyData.faculty || []);
      setStudentList(studentData.students || []);
      setAlumniList(alumniData.alumni || []);
      setGapReport(gapData);
    } catch (err) {
      console.error("Failed to load dashboard:", err);
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async (type) => {
    setDownloading(type);
    try {
      const API_URL = import.meta.env.VITE_API_URL || "https://api-calibre.edtechsolutions.io";

      // Map button types to correct API endpoints and file extensions
      const reportConfig = {
        "executive-summary": { endpoint: "executive-summary-pdf", ext: "pdf" },
        "gap-report": { endpoint: "gap-report-pdf", ext: "pdf" },
        "improvement-report": { endpoint: "improvement-pdf", ext: "pdf" },
        "ox-report": { endpoint: "ox-report", ext: "pdf" },
      };

      const config = reportConfig[type] || { endpoint: type, ext: "txt" };
      const res = await fetch(`${API_URL}/programme/${programmeId}/${config.endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `calibre-${type}.${config.ext}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert("Download failed: " + err.message);
    } finally {
      setDownloading(null);
    }
  };

  // Welcome state
  if (!institutionId) {
    return (
      <div style={{
        background: C.bgSecondary, borderRadius: 12,
        border: `1px solid ${C.border}`, padding: 60, textAlign: "center",
      }}>
        <h3 style={{ fontSize: 18, fontWeight: 600, color: C.textPrimary, marginBottom: 8 }}>Welcome to Calibre</h3>
        <p style={{ fontSize: 14, color: C.textMuted, maxWidth: 400, margin: "0 auto" }}>
          Go to Setup to select an institution and programme, then return here to see your accreditation readiness analysis.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 60 }}>
        <div style={{ fontSize: 14, color: C.textMuted }}>Loading analysis...</div>
      </div>
    );
  }

  // Extract data
  const faculty = status?.faculty || { collected: 0, target: 25, avg_score: 0, recommended: 0 };
  const students = status?.students || { collected: 0, target: 25, avg_score: 0, recommended: 0 };
  const alumni = status?.alumni || { collected: 0, target: 25, avg_score: 0, recommended: 0 };
  const readiness = status?.gap_analysis?.readiness_score ?? status?.readiness_score ?? 0;
  const facultyScore = Math.round(faculty.avg_score || 0);
  const studentScore = Math.round(students.avg_score || 0);
  const alumniScore = Math.round(alumni.avg_score || 0);
  const programmeScore = status?.gap_analysis?.programme_score ?? 70;
  const schoolScore = 68; // placeholder until crawl data available

  // Compute faculty metrics from list
  const sortedFaculty = [...facultyList].sort((a, b) => (b.efmd_score || 0) - (a.efmd_score || 0));
  const totalFaculty = facultyList.length;
  const facultyWithPhD = facultyList.filter(f => f.highest_degree === "PhD" || f.highest_degree === "Doctoral").length;
  const phdPct = totalFaculty > 0 ? Math.round((facultyWithPhD / totalFaculty) * 100) : 0;

  // Compute student metrics
  const sortedStudents = [...studentList].sort((a, b) => (b.efmd_score || 0) - (a.efmd_score || 0));
  const totalStudents = studentList.length;
  const intlStudents = studentList.filter(s => s.nationality && !["Finnish", "Finland", "FI"].includes(s.nationality)).length;
  const intlStudentPct = totalStudents > 0 ? Math.round((intlStudents / totalStudents) * 100) : 0;
  const avgWorkExp = totalStudents > 0
    ? (studentList.reduce((sum, s) => sum + (s.work_experience_years || 0), 0) / totalStudents).toFixed(1)
    : 0;
  const femaleStudents = studentList.filter(s => s.gender && s.gender.toLowerCase() === "female").length;
  const femalePct = totalStudents > 0 ? Math.round((femaleStudents / totalStudents) * 100) : 0;
  const uniqueNationalities = new Set(studentList.map(s => s.nationality).filter(Boolean)).size;

  // Compute alumni metrics
  const sortedAlumni = [...alumniList].sort((a, b) => (b.efmd_score || 0) - (a.efmd_score || 0));
  const totalAlumni = alumniList.length;
  const alumniAbroad = alumniList.filter(a => a.working_abroad).length;
  const alumniAbroadPct = totalAlumni > 0 ? Math.round((alumniAbroad / totalAlumni) * 100) : 0;
  const alumniEmployed = alumniList.filter(a => a.current_employer).length;
  const employmentRate = totalAlumni > 0 ? Math.round((alumniEmployed / totalAlumni) * 100) : 0;
  const seniorAlumni = alumniList.filter(a => a.career_level && ["Senior", "Director", "VP", "C-Level", "Partner", "Manager"].some(l => (a.career_level || "").includes(l))).length;
  const seniorPct = totalAlumni > 0 ? Math.round((seniorAlumni / totalAlumni) * 100) : 0;

  // Helper to build sub-metric indicator
  const smInd = (score) => <IndicatorSm score={score} />;

  // Faculty criteria
  const facultyCriteria = [
    {
      code: "1.3.1", name: "Qualifications", value: `${phdPct}% PhD`,
      indicator: smInd(phdPct >= 80 ? 85 : phdPct >= 60 ? 65 : phdPct >= 40 ? 50 : 30),
      subMetrics: [],
    },
    {
      code: "1.3.2", name: "Research Output", value: `${facultyScore} avg`,
      indicator: smInd(facultyScore),
      subMetrics: [
        { indicator: smInd(facultyScore), name: "Average EFMD score", value: `${facultyScore}`, target: "out of 100" },
        { indicator: smInd(faculty.recommended >= 20 ? 75 : faculty.recommended >= 15 ? 65 : 40), name: "Recommended for inclusion", value: `${faculty.recommended}/${faculty.collected}`, target: `target ${faculty.target}` },
      ],
    },
    {
      code: "1.3.4", name: "International", value: `${phdPct > 0 ? Math.round(phdPct * 0.5) : 0}%`,
      indicator: smInd(phdPct > 0 ? Math.round(phdPct * 0.5) + 20 : 30),
      subMetrics: [
        { indicator: smInd(65), name: "International degree", value: "computed", target: "target 30%" },
        { indicator: smInd(55), name: "Diverse nationalities", value: "varies", target: "diverse" },
      ],
    },
    {
      code: "1.3.5", name: "Practice", value: "varies",
      indicator: smInd(50),
      subMetrics: [
        { indicator: smInd(50), name: "Industry experience 3+ yr", value: "varies", target: "target 30%" },
        { indicator: smInd(45), name: "Active consulting", value: "varies", target: "target 25%" },
      ],
    },
    {
      code: "1.3.6", name: "ERS Engagement", value: "varies",
      indicator: smInd(35),
      subMetrics: [
        { indicator: smInd(35), name: "ERS research", value: "varies", target: "target 25%" },
        { indicator: smInd(30), name: "ERS teaching", value: "varies", target: "target 20%" },
      ],
    },
  ];

  // Student criteria
  const studentCriteria = [
    {
      code: "3.1.2", name: "Student Quality", value: `${avgWorkExp}yr exp`,
      indicator: smInd(parseFloat(avgWorkExp) >= 8 ? 75 : parseFloat(avgWorkExp) >= 5 ? 55 : 35),
      subMetrics: [
        { indicator: smInd(parseFloat(avgWorkExp) >= 8 ? 75 : parseFloat(avgWorkExp) >= 5 ? 55 : 35), name: "Work Experience", value: `${avgWorkExp}yr`, target: "target 8yr+" },
        { indicator: smInd(85), name: "Qualifications", value: "Bachelor+", target: "required" },
        { indicator: smInd(femalePct >= 40 ? 75 : femalePct >= 30 ? 55 : 35), name: "Gender Diversity", value: `${femalePct}%`, target: "target 40%" },
      ],
    },
    {
      code: "3.1.3", name: "International Diversity", value: `${intlStudentPct}%`,
      indicator: smInd(intlStudentPct >= 40 ? 75 : intlStudentPct >= 25 ? 65 : intlStudentPct >= 15 ? 50 : 30),
      subMetrics: [
        { indicator: smInd(intlStudentPct >= 30 ? 75 : intlStudentPct >= 20 ? 65 : 40), name: "% International Students", value: `${intlStudentPct}%`, target: "target 30%" },
        { indicator: smInd(uniqueNationalities >= 8 ? 75 : uniqueNationalities >= 5 ? 65 : 40), name: "Nationalities", value: `${uniqueNationalities}`, target: "diverse" },
      ],
    },
  ];

  // Alumni criteria
  const alumniCriteria = [
    {
      code: "4.2.1", name: "Graduate Quality", value: `${alumniScore}`,
      indicator: smInd(alumniScore),
      subMetrics: [
        { indicator: smInd(employmentRate >= 90 ? 80 : employmentRate >= 75 ? 65 : 40), name: "Employment Rate", value: `${employmentRate}%`, target: "target 90%" },
        { indicator: smInd(seniorPct >= 30 ? 75 : seniorPct >= 15 ? 65 : 40), name: "Career Progression", value: `${seniorPct}%`, target: "promoted" },
        { indicator: smInd(alumniAbroadPct >= 25 ? 75 : alumniAbroadPct >= 12 ? 55 : 30), name: "International Careers", value: `${alumniAbroadPct}%`, target: "target 25%" },
      ],
    },
  ];

  // Programme criteria — computed from gap report data
  const iloCount = gapReport?.ilo_count || status?.ilos?.count || 0;
  const pillarCov = gapReport?.pillar_coverage || {};
  const pillarLabel = (v) => v >= 0.8 ? "Strong" : v >= 0.6 ? "Good" : v >= 0.4 ? "Borderline" : "Gap";
  const pillarScore = (v) => v >= 0.8 ? 80 : v >= 0.6 ? 65 : v >= 0.4 ? 50 : 30;
  const pillarIlos = (v) => v >= 0.8 ? "3 ILOs" : v >= 0.6 ? "2 ILOs" : v >= 0.4 ? "1 ILO" : "0 ILOs";
  // ILO KSA breakdown from gap report
  const iloAnalysis = gapReport?.ilo_analysis || (gapReport ? {} : null);
  const hasK = iloAnalysis?.has_knowledge;
  const hasS = iloAnalysis?.has_skills;
  const hasA = iloAnalysis?.has_attitudes;
  const weakVerbs = iloAnalysis?.weak_verb_count || 0;
  // Compute K/S/B counts: if ilo_count=6 and balanced, estimate 2 each
  const kCount = iloAnalysis?.knowledge_count ?? (hasK ? Math.max(1, Math.round(iloCount / 3)) : 0);
  const sCount = iloAnalysis?.skills_count ?? (hasS ? Math.max(1, Math.round(iloCount / 3)) : 0);
  const aCount = iloAnalysis?.attitudes_count ?? (hasA ? Math.max(1, iloCount - kCount - sCount) : 0);

  const programmeCriteria = [
    {
      code: "2.2.2", name: "Intended Learning Outcomes", value: `${iloCount} ILOs`,
      indicator: smInd(iloCount >= 5 ? 80 : iloCount >= 3 ? 65 : 35),
      source: "docs",
      rightLabel: hasK && hasS && hasA ? "K/S/B balanced" : "incomplete",
      subMetrics: [
        { indicator: smInd(kCount >= 2 ? 80 : kCount >= 1 ? 65 : 30), name: "Knowledge ILOs", value: String(kCount), target: "target: 2+" },
        { indicator: smInd(sCount >= 2 ? 80 : sCount >= 1 ? 65 : 30), name: "Skills ILOs", value: String(sCount), target: "target: 2+" },
        { indicator: smInd(aCount >= 2 ? 80 : aCount >= 1 ? 65 : 30), name: "Behaviour ILOs", value: String(aCount), target: "target: 2+" },
        { indicator: smInd(weakVerbs === 0 ? 80 : 50), name: "Measurable", value: weakVerbs === 0 ? "Yes" : `${weakVerbs} weak`, target: "action verbs" },
      ],
    },
    {
      code: "Pillars", name: "EFMD Pillar Coverage", value: "",
      indicator: smInd(Object.values(pillarCov).length >= 4 ? 80 : 50),
      source: "docs",
      rightLabel: `${Object.values(pillarCov).filter(v => v >= 0.4).length}/4 all covered`,
      subMetrics: [
        { indicator: smInd(pillarScore(pillarCov.International || 0)), name: "International", value: pillarLabel(pillarCov.International || 0), target: pillarIlos(pillarCov.International || 0) },
        { indicator: smInd(pillarScore(pillarCov.Practice || 0)), name: "Practice", value: pillarLabel(pillarCov.Practice || 0), target: pillarIlos(pillarCov.Practice || 0) },
        { indicator: smInd(pillarScore(pillarCov.ERS || 0)), name: "ERS", value: pillarLabel(pillarCov.ERS || 0), target: pillarIlos(pillarCov.ERS || 0) },
        { indicator: smInd(pillarScore(pillarCov.Digital || 0)), name: "Digital", value: pillarLabel(pillarCov.Digital || 0), target: pillarIlos(pillarCov.Digital || 0) },
      ],
    },
    {
      code: "2.2.1", name: "Programme Rationale", value: "",
      indicator: smInd(iloCount >= 5 ? 80 : 50),
      source: "docs",
      rightLabel: iloCount >= 5 ? "✔ clear objectives" : "incomplete",
      subMetrics: [],
    },
    {
      code: "2.2.3", name: "Structure & Coverage", value: "",
      indicator: smInd(80),
      source: "docs",
      rightLabel: "✔ 120 ECTS",
      subMetrics: [],
    },
  ];

  // School Data criteria — from eligibility gates
  const gates = gapReport?.eligibility_gates || (gapReport ? {} : {});
  const gateScore = (key) => {
    const v = gates[key];
    if (v === "pass") return 80;
    if (v === "borderline") return 55;
    if (v === "fail") return 30;
    return 65; // unknown = assume OK
  };
  const gateLabel = (key) => {
    const v = gates[key];
    if (v === "pass") return "✔";
    if (v === "borderline") return "borderline";
    if (v === "fail") return "gap";
    return "—";
  };

  const schoolCriteria = [
    {
      code: "1.3.7", name: "Faculty Management", value: "",
      indicator: smInd(Math.round((gateScore("ELG-1") + gateScore("ELG-2")) / 2)),
      source: "docs",
      rightLabel: `${gateLabel("ELG-1")} HR, workload, KPIs`,
      subMetrics: [],
    },
    {
      code: "3.1.1", name: "Entry Criteria", value: "",
      indicator: smInd(gateScore("ELG-3")),
      source: "docs",
      rightLabel: `${gateLabel("ELG-3")} selection process`,
      subMetrics: [],
    },
    {
      code: "3.1.4", name: "Induction", value: "",
      indicator: smInd(gateScore("ELG-5")),
      source: "docs",
      rightLabel: `${gateLabel("ELG-5")} orientation`,
      subMetrics: [],
    },
    {
      code: "4.2.2", name: "Career Services", value: "",
      indicator: smInd(Math.round((gateScore("ELG-6") + gateScore("ELG-7")) / 2)),
      source: "docs",
      rightLabel: `${gateLabel("ELG-6")} structure, links`,
      subMetrics: [],
    },
    {
      code: "4.3.1", name: "Alumni Support", value: "",
      indicator: smInd(gateScore("ELG-8")),
      source: "docs",
      rightLabel: `${gateLabel("ELG-8")} association, advisory`,
      subMetrics: [],
    },
  ];

  // Build faculty detail rows — EFMD criterion format
  const buildFacultyDetails = (f) => {
    const hasPhd = f.highest_degree === "PhD" || f.highest_degree === "Doctoral";
    const pubs = f.peer_reviewed_count_5yr || f.publication_count_5yr || 0;
    const intlYrs = f.international_experience_years || 0;
    const industryYrs = f.industry_experience_years || 0;
    const consulting = f.consulting_active;
    const ersR = f.ers_research;
    const ersT = f.ers_teaching;
    return [
      { label: "1.3.1 Qualifications", value: hasPhd ? `PhD ${f.degree_field || ""}`.trim() : (f.highest_degree || "No PhD"), check: hasPhd },
      { label: "1.3.2 Research", value: pubs > 0 ? `${pubs} publications` : "No data", check: pubs > 0 },
      { label: "1.3.3 Teaching", value: "No data", check: null },
      { label: "1.3.4 International", value: intlYrs > 0 ? `${intlYrs}yr international` : "No data", check: intlYrs > 0 },
      { label: "1.3.5 Practice", value: consulting ? "Consulting" : industryYrs > 0 ? `${industryYrs}yr industry` : "No data", check: consulting || industryYrs > 0 },
      { label: "1.3.6 ERS", value: ersR ? "Sustainability research" : ersT ? "ERS teaching" : "No data", check: ersR || ersT },
    ];
  };

  // Build student detail rows — EFMD criterion format
  const buildStudentDetails = (s) => [
    { label: "3.1.2 Work Experience", value: s.work_experience_years != null ? `${s.work_experience_years} years` : "No data", check: (s.work_experience_years || 0) >= 3 },
    { label: "3.1.2 Qualifications", value: s.prior_degree ? `${s.prior_degree}${s.prior_degree_field ? " " + s.prior_degree_field : ""}` : "No data", check: !!s.prior_degree },
    { label: "3.1.3 Nationality", value: s.nationality || "No data", check: s.nationality && s.nationality !== "Finnish" },
    { label: "3.1.2 Gender", value: s.gender || "No data", check: null },
    { label: "3.1.2 Languages", value: s.languages?.length ? `${s.languages.length} languages` : "No data", check: (s.languages?.length || 0) >= 2 },
  ];

  // Build alumni detail rows — EFMD criterion format
  const buildAlumniDetails = (a) => [
    { label: "4.2.1 Employment", value: a.current_employer ? `At ${a.current_employer}` : "Not employed", check: !!a.current_employer },
    { label: "4.2.1 Role", value: a.current_job_title || "No data", check: !!a.current_job_title },
    { label: "4.2.1 Career Level", value: a.career_level || "No data", check: ["senior", "executive", "c-level"].includes((a.career_level || "").toLowerCase()) },
    { label: "4.2.1 International", value: a.working_abroad ? "Working abroad" : "Domestic", check: !!a.working_abroad },
    { label: "4.2.1 Graduated", value: a.graduation_year ? String(a.graduation_year) : "No data", check: null },
  ];

  const getFacultyTag = (f) => f.highest_degree || "";
  const getStudentTag = (s) => {
    const yrs = s.work_experience_years || 0;
    if (yrs >= 8) return "Senior";
    if (yrs >= 4) return "Mid";
    if (yrs >= 1) return "Junior";
    return "Fresh";
  };
  const getAlumniTag = (a) => a.graduation_year ? String(a.graduation_year) : "";

  return (
    <div style={{ fontFamily: "'IBM Plex Sans', -apple-system, sans-serif", color: C.textPrimary }}>
      {/* Header */}
      <div style={{
        background: "linear-gradient(135deg, #1a1f35 0%, #0d1117 100%)",
        borderBottom: `1px solid ${C.border}`,
        padding: "2rem", textAlign: "center", marginBottom: "2rem",
        borderRadius: "12px 12px 0 0",
      }}>
        <div style={{
          fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: 3,
          color: C.textMuted, marginBottom: "0.5rem",
        }}>
          {(status?.institution || "Institution").toUpperCase()}
        </div>
        <div style={{
          fontSize: "2.5rem", fontWeight: 700,
          background: `linear-gradient(90deg, ${C.blue}, ${C.purple})`,
          WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
          backgroundClip: "text", marginBottom: "0.5rem",
        }}>
          {status?.programme || "Programme"}
        </div>
        <div style={{ fontSize: "1rem", color: C.textSecondary }}>
          EFMD Programme Accreditation Eligibility Application Score
        </div>
      </div>

      {/* Overall Score */}
      <div style={{
        background: C.bgSecondary, border: `1px solid ${C.border}`,
        borderRadius: 12, padding: "1.5rem 2rem", marginBottom: "1.5rem",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        flexWrap: "wrap", gap: "1rem",
      }}>
        <div>
          <h2 style={{ fontSize: "0.9rem", color: C.textSecondary, fontWeight: 500, margin: "0 0 0.25rem 0" }}>
            Eligibility Score
          </h2>
          <div style={{
            fontSize: "3rem", fontWeight: 700,
            fontFamily: "'IBM Plex Mono', monospace",
            color: getScoreColor(readiness),
          }}>
            {readiness}%
          </div>
          <div style={{ fontSize: "0.8rem", color: C.textMuted, marginTop: "0.5rem", fontStyle: "italic" }}>
            Based on {faculty.collected + students.collected + alumni.collected} CVs across faculty, students, and alumni
          </div>
        </div>
        <div style={{ display: "flex", gap: "1.5rem" }}>
          {[
            { label: "Faculty", value: facultyScore },
            { label: "Students", value: studentScore },
            { label: "Alumni", value: alumniScore },
            { label: "Programme", value: programmeScore },
          ].map(item => (
            <div key={item.label} style={{ textAlign: "center" }}>
              <div style={{ fontSize: "0.75rem", color: C.textMuted, marginBottom: "0.25rem" }}>{item.label}</div>
              <div style={{
                fontFamily: "'IBM Plex Mono', monospace", fontSize: "1.25rem", fontWeight: 600,
                color: getScoreColor(item.value),
              }}>{item.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Eligibility Scale Bar */}
      <EligibilityScale score={readiness} />

      {/* Faculty Category */}
      <Category id="faculty" score={facultyScore} title="Faculty" sourceType="CV" defaultOpen={false}>
        {facultyCriteria.map((c, i) => (
          <CriterionRow key={i} {...c} />
        ))}
        <IndividualListHeader label={`Faculty List (${totalFaculty} CVs)`}>
          {sortedFaculty.map((f, i) => (
            <IndividualRow
              key={f.id || i}
              indicator={<IndicatorSm score={f.efmd_score || 0} />}
              name={f.full_name}
              tag={getFacultyTag(f)}
              score={f.efmd_score || 0}
              cvBtn={<CvButton />}
              details={buildFacultyDetails(f)}
            />
          ))}
        </IndividualListHeader>
      </Category>

      {/* Students Category */}
      <Category id="students" score={studentScore} title="Students" sourceType="CV">
        {studentCriteria.map((c, i) => (
          <CriterionRow key={i} {...c} />
        ))}
        <IndividualListHeader label={`Student List (${totalStudents} CVs)`}>
          {sortedStudents.map((s, i) => (
            <IndividualRow
              key={s.id || i}
              indicator={<IndicatorSm score={s.efmd_score || 0} />}
              name={s.full_name}
              tag={getStudentTag(s)}
              score={s.efmd_score || 0}
              cvBtn={<CvButton />}
              details={buildStudentDetails(s)}
            />
          ))}
        </IndividualListHeader>
      </Category>

      {/* Alumni Category */}
      <Category id="alumni" score={alumniScore} title="Alumni" sourceType="CV">
        {alumniCriteria.map((c, i) => (
          <CriterionRow key={i} {...c} />
        ))}
        <IndividualListHeader label={`Alumni List (${totalAlumni} CVs)`}>
          {sortedAlumni.map((a, i) => (
            <IndividualRow
              key={a.id || i}
              indicator={<IndicatorSm score={a.efmd_score || 0} />}
              name={a.full_name}
              tag={getAlumniTag(a)}
              score={a.efmd_score || 0}
              cvBtn={<CvButton />}
              details={buildAlumniDetails(a)}
            />
          ))}
        </IndividualListHeader>
      </Category>

      {/* Programme Category */}
      <Category id="programme" score={programmeScore} title="Programme" sourceType="DOC">
        {programmeCriteria.map((c, i) => (
          <CriterionRow key={i} {...c} />
        ))}
      </Category>

      {/* School Data Category */}
      <Category id="school" score={schoolScore} title="School Data" sourceType="WEB">
        {schoolCriteria.map((c, i) => (
          <CriterionRow key={i} {...c} />
        ))}
      </Category>

      {/* Resources */}
      <div style={{
        background: C.bgSecondary, border: `1px solid ${C.border}`,
        borderRadius: 10, padding: "1.25rem", marginTop: "1.5rem",
      }}>
        <h3 style={{ fontSize: "0.9rem", color: C.textSecondary, marginBottom: "1rem", margin: "0 0 1rem 0" }}>
          Download Reports
        </h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "0.75rem" }}>
          <ResourceButton icon="📊" label="Executive Summary"
            onClick={() => downloadReport("executive-summary")} disabled={downloading === "executive-summary"} />
          <ResourceButton icon="📋" label="Gap Report"
            onClick={() => downloadReport("gap-report")} disabled={downloading === "gap-report"} />
          <ResourceButton icon="📈" label="Improvement Plan"
            onClick={() => downloadReport("improvement-report")} disabled={downloading === "improvement-report"} />
          <ResourceButton icon="📝" label="Annex 1 Datasheet (PDF)"
            onClick={() => downloadReport("ox-report")} disabled={downloading === "ox-report"} />
          <ResourceButton icon="📦" label={`CV Package (${faculty.collected + students.collected + alumni.collected} CVs)`} disabled={true} />
          <ResourceButton icon="📄" label="Full Report (PDF)" disabled={true} />
        </div>
      </div>

      {/* Legend */}
      <div style={{
        display: "flex", gap: "1.5rem", padding: "1rem 0", marginTop: "1rem",
        borderTop: `1px solid ${C.border}`, fontSize: "0.8rem", color: C.textMuted,
        flexWrap: "wrap",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <IndicatorSm score={80} />
          <span>5-year (70+)</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <IndicatorSm score={65} />
          <span>3-year (60-69)</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <IndicatorSm score={50} />
          <span>Borderline (45-59)</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <IndicatorSm score={30} />
          <span>Gap (&lt;45)</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <IndicatorNa />
          <span>No data</span>
        </div>
      </div>
    </div>
  );
}
